import { useTranslation } from 'react-i18next'
import { X, Bell, FileCheck, TestTube, ArrowRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { FeatureWithActions, KanbanColumn } from '@/shared/types'

export interface ActionItem {
  id: string
  type: 'validation' | 'testing' | 'transition'
  featureId: string
  featureShortId: string
  featureName: string
  count: number
  targetColumn?: KanbanColumn
}

interface ActionModalProps {
  isOpen: boolean
  onClose: () => void
  actions: ActionItem[]
  onActionClick: (action: ActionItem) => void
}

function getActionsFromFeatures(features: FeatureWithActions[]): ActionItem[] {
  const actions: ActionItem[] = []

  for (const feature of features) {
    // Skip features in backlog or done
    if (feature.kanban_column === 'backlog' || feature.kanban_column === 'done') {
      continue
    }

    // US pending validation (scoping phase)
    if (feature.kanban_column === 'scoping' && feature.actionCounts.pendingValidation > 0) {
      actions.push({
        id: `validation-${feature.id}`,
        type: 'validation',
        featureId: feature.id,
        featureShortId: feature.short_id,
        featureName: feature.name,
        count: feature.actionCounts.pendingValidation,
      })
    }

    // US to test (testing phase)
    if (feature.kanban_column === 'testing' && feature.actionCounts.testing > 0) {
      actions.push({
        id: `testing-${feature.id}`,
        type: 'testing',
        featureId: feature.id,
        featureShortId: feature.short_id,
        featureName: feature.name,
        count: feature.actionCounts.testing,
      })
    }

    // Feature ready to transition (scoping → design, no pending validation)
    if (
      feature.kanban_column === 'scoping' &&
      feature.actionCounts.pendingValidation === 0 &&
      feature.actionCounts.totalUS > 0
    ) {
      actions.push({
        id: `transition-${feature.id}`,
        type: 'transition',
        featureId: feature.id,
        featureShortId: feature.short_id,
        featureName: feature.name,
        count: 0,
        targetColumn: 'design',
      })
    }

    // Feature ready to transition (design → development)
    if (feature.kanban_column === 'design') {
      actions.push({
        id: `transition-${feature.id}`,
        type: 'transition',
        featureId: feature.id,
        featureShortId: feature.short_id,
        featureName: feature.name,
        count: 0,
        targetColumn: 'development',
      })
    }

    // Feature ready to move to done (testing, no US left to test)
    if (
      feature.kanban_column === 'testing' &&
      feature.actionCounts.testing === 0 &&
      feature.actionCounts.totalUS > 0
    ) {
      actions.push({
        id: `transition-${feature.id}`,
        type: 'transition',
        featureId: feature.id,
        featureShortId: feature.short_id,
        featureName: feature.name,
        count: 0,
        targetColumn: 'done',
      })
    }
  }

  return actions
}

function ActionItemRow({
  action,
  onClick,
  isLast,
}: {
  action: ActionItem
  onClick: () => void
  isLast: boolean
}) {
  const { t } = useTranslation()

  const typeConfig = {
    validation: {
      icon: FileCheck,
      label: t('actionModal.typeValidation'),
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      description: t('actionModal.descValidation', { count: action.count }),
      buttonText: t('actionModal.viewFeature'),
    },
    testing: {
      icon: TestTube,
      label: t('actionModal.typeTesting'),
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      description: t('actionModal.descTesting', { count: action.count }),
      buttonText: t('actionModal.viewFeature'),
    },
    transition: {
      icon: ArrowRight,
      label: t('actionModal.typeTransition'),
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      description: t('actionModal.descTransition', { column: t(`kanban.columns.${action.targetColumn}`) }),
      buttonText: t('actionModal.moveFeature'),
    },
  }

  const config = typeConfig[action.type]
  const Icon = config.icon

  return (
    <div
      className={cn(
        'flex flex-col gap-2 py-4',
        !isLast && 'border-b border-border'
      )}
    >
      {/* Header: Feature name + Type badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-foreground-muted" />
          <span className="text-sm font-medium text-foreground">{action.featureName}</span>
        </div>
        <span
          className={cn(
            'rounded px-2 py-1 text-xs font-medium',
            config.bgColor,
            config.textColor
          )}
        >
          {config.label}
        </span>
      </div>

      {/* Description */}
      <p className="text-[13px] text-foreground-muted">{config.description}</p>

      {/* Action button */}
      <button
        onClick={onClick}
        className="w-fit rounded-md bg-accent px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-accent/90"
      >
        {config.buttonText}
      </button>
    </div>
  )
}

export function ActionModal({ isOpen, onClose, actions, onActionClick }: ActionModalProps) {
  const { t } = useTranslation()

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
    >
      <div
        className="flex h-full w-[400px] max-w-[90vw] flex-col overflow-hidden bg-white shadow-xl animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-6">
          <div className="flex items-center gap-3">
            {/* Icon container */}
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-orange-50">
              <Bell className="h-[22px] w-[22px] text-accent" />
            </div>
            {/* Title group */}
            <div className="flex flex-col gap-0.5">
              <h2 className="text-lg font-semibold text-foreground">
                {t('actionModal.title')}
              </h2>
              <p className="text-sm text-foreground-muted">
                {t('actionModal.subtitle', { count: actions.length })}
              </p>
            </div>
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-background-secondary text-foreground-muted transition-colors hover:bg-border hover:text-foreground"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-6">
          {actions.map((action, index) => (
            <ActionItemRow
              key={action.id}
              action={action}
              onClick={() => onActionClick(action)}
              isLast={index === actions.length - 1}
            />
          ))}
        </div>

        {/* Footer - sticky at bottom */}
        <div className="mt-auto border-t border-border p-4 px-6">
          <button
            onClick={onClose}
            className="rounded-lg border border-border bg-background-secondary px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-border"
          >
            {t('actionModal.dismiss')}
          </button>
        </div>
      </div>
    </div>
  )
}

export { getActionsFromFeatures }
